import React, { ChangeEvent, FC, useState, useEffect } from 'react';
import AuthForm from '../layouts/auth-form';
import Input from 'components/commercetools-ui/atoms/input';
import Button from 'components/commercetools-ui/atoms/button';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import useAuthProps from './hooks/useAuthProps';
import { EMAIL_PATTERN } from '../company-registration/data';
import Typography from 'components/commercetools-ui/atoms/typography';
import { sdk } from 'sdk';
import PasswordResetConfirm from './password-reset-confirm';

type ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => void;

const ResetPassword: FC<any> = ({
  setBootLogin,
  setShowResetPopup,
  setShowEntirePopup,
  failed,
  defaultLoginModal = false,
}) => {
  const { translate } = useTranslation();
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const extensions = sdk.composableCommerce;

  const [error, setError] = useState<string>();
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  const handleBlur = () => {
    const emailRegex = EMAIL_PATTERN;
    if (!email || !emailRegex.test(email)) {
      setError(translate('error.auth.email.wrong'));
    } else {
      setError('');
    }
  };

  const handleResetSubmit = async () => {
    if (email) {
      try {
        setError('');
        setLoading(true);
        const responseBody: any = await extensions.account.ResetPasswordAuth0(
          JSON.stringify({ email: email.toLowerCase() }),
        );
        if (responseBody?.data?.statusCode === 200) {
          setLoading(false);
          setShowConfirmationPopup(!showConfirmationPopup);
        } else {
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error('Reset Pasword Error:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setError(translate('error.auth.email.wrong'));
    }
  };

  const onActivateAccount = () => {
    setShowResetPopup(false);
    setError('');
    setEmail('');
    if (!defaultLoginModal) {
      setBootLogin(false);
    }
  };

  const onLogin = () => {
    setShowResetPopup(false);
  };

  const handleOnClosePopup = () => {
    setShowResetPopup(false, () => {
      setError('');
      setEmail('');
    });
    setShowEntirePopup(false);
    if (!defaultLoginModal) {
      setBootLogin(false);
    }
  };

  const goBackToLogin = () => {
    setResetting(false);
  };

  const authProps = useAuthProps({
    handleLoginSubmit: () => {},
    handleResetSubmit,
    resetting,
    goBackToLogin,
    handleOnClosePopup,
  });

  const handleChange = (name: string, value: string) => {
    const emailRegex = EMAIL_PATTERN;
    if (!value || !emailRegex.test(value)) {
      setError(translate('error.auth.email.wrong'));
    } else {
      setError('');
    }

    setEmail(value);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    handleChange('email', pastedData);
  };

  const handleInputChange: ChangeHandler = (event) => {
    const { name, value } = event.target;
    handleChange(name, value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (failed) {
      setError(failed);
    }
  }, [failed]);

  return showConfirmationPopup ? (
    <PasswordResetConfirm
      {...authProps}
      defaultLoginModal={true}
      setShowConfirmationPopup={setShowConfirmationPopup}
      handleOnClosePopup={onLogin}
      handleModalCancel={() => setShowEntirePopup(false)}
      email={email}
    />
  ) : (
    <div>
      <AuthForm
        {...authProps}
        title="Reset Password"
        error={error}
        loading={loading}
        onSubmit={handleResetSubmit}
        buttonLabel="Get Reset Link"
        handleModalCancel={() => setShowEntirePopup(false)}
      >
        <Typography className="text-14">Enter the email address associated with your account.</Typography>
        <Input
          name={'email'}
          placeholder={translate('common.emailAddress')}
          value={(email as string) ?? ''}
          required
          onBlur={handleBlur}
          onChange={handleInputChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          className={`${error ? '!border-red-500' : '!border-[#A7A7A7]'} !rounded-[6px] `}
        />
      </AuthForm>

      <div className="flex w-full flex-row justify-between gap-4">
        <Button
          className={`relative !my-2 h-44 w-full cursor-pointer rounded-none  border-2 border-black hover:bg-transparent bg-transparent !px-25 !py-13 text-center text-14 !text-black`}
          onClick={onActivateAccount}
        >
          {'Activate Account'}
        </Button>
        <Button
          className={`relative !my-2 h-44 w-full cursor-pointer rounded-none  border-2 border-black bg-transparent hover:bg-transparent !px-25 !py-13 text-center text-14 !text-black`}
          onClick={onLogin}
        >
          {'Log In'}
        </Button>
      </div>
    </div>
  );
};

export default ResetPassword;
